import { redirect } from "@tanstack/react-router";
import { can } from "@/lib/staff-roles";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { BedDouble, CalendarDays, Pencil, RefreshCw, RotateCcw, Save, Ship, Trash2, X } from "lucide-react";
import { bookingType, estimateBooking } from "@/lib/booking-revenue";
import { DOCK_LOCATION } from "@/lib/booking-approval";
import { FLEET } from "@/lib/fleet";
import { occupiedNights } from "@/lib/lodge-inventory";
import { assignBoats } from "@/lib/boat-assignments.functions";
import { ROOMS } from "@/lib/rooms";
import { assignRooms } from "@/lib/room-assignments.functions";
import { BookingChargesPanel } from "@/components/admin/BookingChargesPanel";
import { BookingHistoryPanel } from "@/components/admin/BookingHistoryPanel";


import {
  cancelBookingRequest,
  reinstateBookingRequest,
  updateBookingRequest,
} from "@/lib/booking-admin.functions";

export const Route = createFileRoute("/_authenticated/admin/bookings")({
  beforeLoad: ({ context }) => {
    if (!can(context.staffRole, "viewBookings")) {
      throw redirect({ to: "/admin/calendar" });
    }
  },
  head: () => ({
    meta: [
      { title: "Manage Bookings — Coffman Cove Lodging" },
      { name: "description", content: "Staff tools to edit or cancel approved bookings." },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: AdminBookings,
});

type Booking = {
  id: string;
  name: string;
  email: string;
  phone: string | null;
  arrival_date: string | null;
  departure_date: string | null;
  party_size: number | null;
  package: string | null;
  source: string | null;
  notes: string | null;
  status: string;
  created_at: string;
};

type Draft = {
  name: string;
  email: string;
  phone: string;
  arrival_date: string;
  departure_date: string;
  party_size: string;
  package: string;
  notes: string;
};

const money = (cents: number) =>
  (cents / 100).toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });

function prettyDay(iso: string | null) {
  if (!iso) return "—";
  return new Date(`${iso.slice(0, 10)}T00:00:00Z`).toLocaleDateString("en-US", {
    timeZone: "UTC",
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function toDraft(b: Booking): Draft {
  return {
    name: b.name ?? "",
    email: b.email ?? "",
    phone: b.phone ?? "",
    arrival_date: b.arrival_date?.slice(0, 10) ?? "",
    departure_date: b.departure_date?.slice(0, 10) ?? "",
    party_size: b.party_size == null ? "" : String(b.party_size),
    package: b.package ?? "",
    notes: b.notes ?? "",
  };
}

type Assignment = { booking_id: string; boat_name: string; night: string };
type RoomAssignment = { booking_id: string; room_id: string; night: string };

function AdminBookings() {
  const { staffRole } = Route.useRouteContext();
  const canEdit = can(staffRole, "manageBookings");
  const [rows, setRows] = useState<Booking[] | null>(null);
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [boatDraft, setBoatDraft] = useState<Record<string, string[]>>({});
  const [roomAssignments, setRoomAssignments] = useState<RoomAssignment[]>([]);
  const [roomDraft, setRoomDraft] = useState<Record<string, string[]>>({});
  const [loading, setLoading] = useState(false);
  const [showCancelled, setShowCancelled] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const saveBooking = useServerFn(updateBookingRequest);
  const cancelBooking = useServerFn(cancelBookingRequest);
  const reinstateBooking = useServerFn(reinstateBookingRequest);
  const saveBoats = useServerFn(assignBoats);
  const saveRooms = useServerFn(assignRooms);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("booking_requests")
        .select("id,name,email,phone,arrival_date,departure_date,party_size,package,source,notes,status,created_at")
        .in("status", ["approved", "cancelled"])
        .order("arrival_date", { ascending: true })
        .limit(500);
      if (error) throw error;
      setRows((data ?? []) as Booking[]);

      const { data: assigned } = await supabase
        .from("boat_assignments")
        .select("booking_id,boat_name,night")
        .limit(5000);
      const list = ((assigned ?? []) as Assignment[]).map((a) => ({
        ...a,
        night: String(a.night).slice(0, 10),
      }));
      setAssignments(list);
      const grouped: Record<string, string[]> = {};
      for (const a of list) {
        const set = grouped[a.booking_id] ?? [];
        if (!set.includes(a.boat_name)) set.push(a.boat_name);
        grouped[a.booking_id] = set;
      }
      setBoatDraft(grouped);

      const { data: roomRows } = await supabase
        .from("room_assignments")
        .select("booking_id,room_id,night")
        .limit(5000);
      const rlist = ((roomRows ?? []) as RoomAssignment[]).map((a) => ({
        ...a,
        night: String(a.night).slice(0, 10),
      }));
      setRoomAssignments(rlist);
      const rgrouped: Record<string, string[]> = {};
      for (const a of rlist) {
        const set = rgrouped[a.booking_id] ?? [];
        if (!set.includes(a.room_id)) set.push(a.room_id);
        rgrouped[a.booking_id] = set;
      }
      setRoomDraft(rgrouped);
    } catch {
      toast.error("Could not load bookings");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const visible = useMemo(
    () => (rows ?? []).filter((r) => (showCancelled ? true : r.status === "approved")),
    [rows, showCancelled],
  );

  /** boat|night -> booking that owns it, so we can grey out taken boats. */
  const takenBy = useMemo(() => {
    const map = new Map<string, string>();
    for (const a of assignments) map.set(`${a.boat_name}|${a.night}`, a.booking_id);
    return map;
  }, [assignments]);

  const conflictsFor = useCallback(
    (b: Booking, boat: string) => {
      for (const night of occupiedNights(b as never)) {
        const owner = takenBy.get(`${boat}|${night}`);
        if (owner && owner !== b.id) return night;
      }
      return null;
    },
    [takenBy],
  );

  async function onSaveBoats(b: Booking) {
    setBusy(b.id);
    try {
      await saveBoats({ data: { bookingId: b.id, boats: boatDraft[b.id] ?? [] } });
      toast.success("Boat assignment saved");
      await load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not assign that boat");
    } finally {
      setBusy(null);
    }
  }

  const roomTakenBy = useMemo(() => {
    const map = new Map<string, string>();
    for (const a of roomAssignments) map.set(`${a.room_id}|${a.night}`, a.booking_id);
    return map;
  }, [roomAssignments]);

  const roomConflictFor = useCallback(
    (b: Booking, roomId: string) => {
      for (const night of occupiedNights(b as never)) {
        const owner = roomTakenBy.get(`${roomId}|${night}`);
        if (owner && owner !== b.id) return night;
      }
      return null;
    },
    [roomTakenBy],
  );

  async function onSaveRooms(b: Booking) {
    setBusy(b.id);
    try {
      await saveRooms({ data: { bookingId: b.id, rooms: roomDraft[b.id] ?? [] } });
      toast.success("Room assignment saved");
      await load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not assign that room");
    } finally {
      setBusy(null);
    }
  }

  async function onSave(id: string) {
    if (!draft) return;
    setBusy(id);
    try {
      const res: any = await saveBooking({
        data: {
          id,
          name: draft.name.trim(),
          email: draft.email.trim(),
          phone: draft.phone.trim() || null,
          arrival_date: draft.arrival_date || null,
          departure_date: draft.departure_date || null,
          party_size: draft.party_size === "" ? null : Number(draft.party_size),
          package: draft.package.trim() || null,
          notes: draft.notes.trim() || null,
        },
      });
      toast.success("Booking updated — calendar and revenue refreshed");
      const released = [...(res?.droppedBoats ?? []), ...(res?.droppedRooms ?? [])];
      if (released.length) {
        toast.warning(`Released on the new dates: ${released.join(", ")}`);
      }
      setEditing(null);
      setDraft(null);
      await load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not save this booking");
    } finally {
      setBusy(null);
    }
  }

  async function onCancel(b: Booking) {
    const reason = window.prompt(`Cancel ${b.name}'s booking? Add an optional reason:`, "");
    if (reason === null) return;
    setBusy(b.id);
    try {
      await cancelBooking({ data: { id: b.id, reason: reason.trim() || undefined } });
      toast.success("Booking cancelled and removed from the calendar");
      await load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not cancel this booking");
    } finally {
      setBusy(null);
    }
  }

  async function onReinstate(b: Booking) {
    setBusy(b.id);
    try {
      await reinstateBooking({ data: { id: b.id } });
      toast.success("Booking put back on the calendar");
      await load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not reinstate this booking");
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="mx-auto max-w-6xl px-4 py-12">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 text-[var(--color-ember)]">
            <CalendarDays size={18} aria-hidden />
            <span className="text-xs font-semibold uppercase tracking-[0.25em]">Staff planning</span>
          </div>
          <h1 className="mt-2 font-display text-3xl">Manage bookings</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Edit dates, party size and rentals, or cancel a stay. Changes flow straight to the{" "}
            <Link to="/admin/calendar" className="underline">
              calendar
            </Link>{" "}
            and the revenue CSV.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-2 text-sm text-muted-foreground">
            <input
              type="checkbox"
              checked={showCancelled}
              onChange={(e) => setShowCancelled(e.target.checked)}
              className="h-4 w-4"
            />
            Show cancelled
          </label>
          <button
            type="button"
            onClick={() => void load()}
            disabled={loading}
            className="inline-flex items-center gap-2 rounded-md border border-border px-3 py-2 text-sm hover:bg-muted disabled:opacity-60"
          >
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} aria-hidden />
            Refresh
          </button>
        </div>
      </header>

      {rows === null ? (
        <p className="mt-8 text-sm text-muted-foreground">Loading…</p>
      ) : visible.length === 0 ? (
        <p className="mt-8 text-sm text-muted-foreground">
          No approved bookings yet. Approve requests in the{" "}
          <Link to="/admin/inbox" className="underline">
            inbox
          </Link>
          .
        </p>
      ) : (
        <ul className="mt-8 space-y-4">
          {visible.map((b) => {
            const est = estimateBooking(b);
            const isEditing = editing === b.id;
            return (
              <li key={b.id} className="rounded-xl border border-border bg-card p-5">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-semibold">{b.name}</span>
                      <span className="rounded-full bg-[var(--color-ember)]/12 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wider text-[var(--color-ember)]">
                        {bookingType(b)}
                      </span>
                      {b.status === "cancelled" && (
                        <span className="rounded-full bg-destructive/15 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wider text-destructive">
                          Cancelled
                        </span>
                      )}
                    </div>
                    <p className="mt-1 text-sm text-muted-foreground">
                      {prettyDay(b.arrival_date)} → {prettyDay(b.departure_date)} · {est.nights} night
                      {est.nights === 1 ? "" : "s"} · {est.anglers} anglers · {est.boats} boat
                      {est.boats === 1 ? "" : "s"}
                      {est.processing ? " · processing" : ""}
                    </p>
                    <p className="mt-1 text-xs text-muted-foreground">
                      Est. {money(est.totalCents)} · {b.email}
                      {b.phone ? ` · ${b.phone}` : ""}
                    </p>
                  </div>
                  {canEdit ? (
                  <div className="flex flex-wrap gap-2">
                    {b.status === "cancelled" ? (
                      <button
                        type="button"
                        onClick={() => void onReinstate(b)}
                        disabled={busy === b.id}
                        className="inline-flex items-center gap-2 rounded-md border border-border px-3 py-2 text-sm hover:bg-muted disabled:opacity-60"
                      >
                        <RotateCcw size={14} aria-hidden /> Reinstate
                      </button>
                    ) : (
                      <>
                        <button
                          type="button"
                          onClick={() => {
                            setEditing(isEditing ? null : b.id);
                            setDraft(isEditing ? null : toDraft(b));
                          }}
                          className="inline-flex items-center gap-2 rounded-md border border-border px-3 py-2 text-sm hover:bg-muted"
                        >
                          {isEditing ? <X size={14} aria-hidden /> : <Pencil size={14} aria-hidden />}
                          {isEditing ? "Close" : "Edit"}
                        </button>
                        <button
                          type="button"
                          onClick={() => void onCancel(b)}
                          disabled={busy === b.id}
                          className="inline-flex items-center gap-2 rounded-md border border-destructive/40 px-3 py-2 text-sm text-destructive hover:bg-destructive/10 disabled:opacity-60"
                        >
                          <Trash2 size={14} aria-hidden /> Cancel
                        </button>
                      </>
                    )}
                  </div>
                  ) : (
                    <p className="text-xs text-muted-foreground">View only — ask a manager to make changes.</p>
                  )}
                </div>

                {b.status !== "cancelled" && (
                  <div className="mt-4 border-t border-border pt-4">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                        Room assignment
                      </span>
                      {canEdit ? (
                      <button
                        type="button"
                        onClick={() => void onSaveRooms(b)}
                        disabled={busy === b.id}
                        className="inline-flex items-center gap-2 rounded-md border border-border px-3 py-1.5 text-xs hover:bg-muted disabled:opacity-60"
                      >
                        <BedDouble size={13} aria-hidden /> Save rooms
                      </button>
                      ) : null}
                    </div>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {ROOMS.map((room) => {
                        const selected = (roomDraft[b.id] ?? []).includes(room.id);
                        const clash = roomConflictFor(b, room.id);
                        const blocked = !!clash && !selected;
                        return (
                          <label
                            key={room.id}
                            title={blocked ? `Already occupied on ${clash}` : room.detail}
                            className={`flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs ${
                              selected
                                ? "border-[var(--color-ember)] bg-[var(--color-ember)]/12 text-[var(--color-ember)]"
                                : blocked
                                  ? "border-border text-muted-foreground opacity-50"
                                  : "border-border hover:bg-muted"
                            }`}
                          >
                            <input
                              type="checkbox"
                              className="h-3.5 w-3.5"
                              checked={selected}
                              disabled={blocked || !canEdit}
                              onChange={(e) =>
                                setRoomDraft((prev) => {
                                  const current = prev[b.id] ?? [];
                                  return {
                                    ...prev,
                                    [b.id]: e.target.checked
                                      ? [...current, room.id]
                                      : current.filter((x) => x !== room.id),
                                  };
                                })
                              }
                            />
                            {room.label}
                            <span className="text-[10px] text-muted-foreground">{room.detail}</span>
                            {blocked ? " · taken" : ""}
                          </label>
                        );
                      })}
                    </div>
                    <p className="mt-2 text-[11px] text-muted-foreground">
                      Rooms 1–8 sleep 2, room 9 sleeps 3, units 10 and 11 are family units with 3 bedrooms. A room can
                      only hold one booking per night.
                    </p>
                  </div>
                )}

                {b.status !== "cancelled" && (
                  <div className="mt-4 border-t border-border pt-4">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                        Boat assignment
                      </span>
                      {canEdit ? (
                      <button
                        type="button"
                        onClick={() => void onSaveBoats(b)}
                        disabled={busy === b.id}
                        className="inline-flex items-center gap-2 rounded-md border border-border px-3 py-1.5 text-xs hover:bg-muted disabled:opacity-60"
                      >
                        <Ship size={13} aria-hidden /> Save boats
                      </button>
                      ) : null}
                    </div>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {FLEET.map((boat) => {
                        const selected = (boatDraft[b.id] ?? []).includes(boat);
                        const clash = conflictsFor(b, boat);
                        const blocked = !!clash && !selected;
                        return (
                          <label
                            key={boat}
                            title={blocked ? `Already out on ${clash}` : undefined}
                            className={`flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs ${
                              selected
                                ? "border-[var(--color-ember)] bg-[var(--color-ember)]/12 text-[var(--color-ember)]"
                                : blocked
                                  ? "border-border text-muted-foreground opacity-50"
                                  : "border-border hover:bg-muted"
                            }`}
                          >
                            <input
                              type="checkbox"
                              className="h-3.5 w-3.5"
                              checked={selected}
                              disabled={blocked || !canEdit}
                              onChange={(e) =>
                                setBoatDraft((prev) => {
                                  const current = prev[b.id] ?? [];
                                  return {
                                    ...prev,
                                    [b.id]: e.target.checked
                                      ? [...current, boat]
                                      : current.filter((x) => x !== boat),
                                  };
                                })
                              }
                            />
                            {boat}
                            {blocked ? " · taken" : ""}
                          </label>
                        );
                      })}
                    </div>
                    <p className="mt-2 text-[11px] text-muted-foreground">
                      A boat can only be on one booking per night — boats already out on these dates are greyed out.
                    </p>
                  </div>
                )}

                {canEdit && b.status !== "cancelled" && <BookingChargesPanel bookingId={b.id} />}

                <BookingHistoryPanel bookingId={b.id} />



                {isEditing && draft && (
                  <form
                    className="mt-4 grid gap-3 border-t border-border pt-4 sm:grid-cols-2"
                    onSubmit={(e) => {
                      e.preventDefault();
                      void onSave(b.id);
                    }}
                  >
                    <Field label="Guest name">
                      <input
                        value={draft.name}
                        onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                        required
                        className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                      />
                    </Field>
                    <Field label="Email">
                      <input
                        type="email"
                        value={draft.email}
                        onChange={(e) => setDraft({ ...draft, email: e.target.value })}
                        required
                        className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                      />
                    </Field>
                    <Field label="Phone">
                      <input
                        value={draft.phone}
                        onChange={(e) => setDraft({ ...draft, phone: e.target.value })}
                        className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                      />
                    </Field>
                    <Field label="Booking type">
                      <input
                        value={draft.package}
                        onChange={(e) => setDraft({ ...draft, package: e.target.value })}
                        className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                      />
                    </Field>
                    <Field label="Arrival">
                      <input
                        type="date"
                        value={draft.arrival_date}
                        onChange={(e) => setDraft({ ...draft, arrival_date: e.target.value })}
                        className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                      />
                    </Field>
                    <Field label="Departure">
                      <input
                        type="date"
                        value={draft.departure_date}
                        onChange={(e) => setDraft({ ...draft, departure_date: e.target.value })}
                        className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                      />
                    </Field>
                    <Field label="Anglers">
                      <input
                        type="number"
                        min={0}
                        max={26}
                        value={draft.party_size}
                        onChange={(e) => setDraft({ ...draft, party_size: e.target.value })}
                        className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                      />
                    </Field>
                    <Field label="Notes (keep the “Boats: 2” and “Fish processing: Yes” lines — rentals are read from them)">
                      <textarea
                        rows={4}
                        value={draft.notes}
                        onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
                        className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                      />
                    </Field>
                    <div className="sm:col-span-2 flex items-center gap-3">
                      <button
                        type="submit"
                        disabled={busy === b.id}
                        className="inline-flex items-center gap-2 rounded-md bg-[var(--color-ember)] px-4 py-2 text-sm font-semibold text-[var(--color-ember-foreground)] disabled:opacity-60"
                      >
                        <Save size={14} aria-hidden /> Save changes
                      </button>
                      <span className="text-xs text-muted-foreground">
                        Saving updates the calendar, revenue pane and CSV export.
                      </span>
                    </div>
                  </form>
                )}
              </li>
            );
          })}
        </ul>
      )}

      <p className="mt-8 text-[11px] text-muted-foreground">{DOCK_LOCATION}</p>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block text-sm">
      <span className="mb-1 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        {label}
      </span>
      {children}
    </label>
  );
}
